import { useMemo, useCallback, useState, useEffect, useRef, type MouseEvent } from 'react'
import { Plus } from 'lucide-react'
import type { TimetableGridEntry, GridDimensions } from './typesV2'
import { DAY_LABELS, DAY_ORDER } from './typesV2'
import { normalizeTimeSlot } from './timeUtils'
import { ENTRY_FIELD_COLORS } from './entryUtils'
import { exclusionHatchStyle, exclusionTitle } from '@/utils/exclusionHatch'

const UNASSIGNED_DAY = '__UNASSIGNED_DAY__'
const UNASSIGNED_TIME = '__UNASSIGNED_TIME__'

export const MAX_FIT_SCALE = 1.1

export function computeFitScale(
  containerWidth: number,
  containerHeight: number,
  contentWidth: number,
  contentHeight: number,
): number {
  if (containerWidth <= 0 || containerHeight <= 0 || contentWidth <= 0 || contentHeight <= 0) return 1
  return Math.min(containerWidth / contentWidth, containerHeight / contentHeight, MAX_FIT_SCALE)
}

interface GridCellEntry {
  entry: TimetableGridEntry
  index: number
}

interface GridCell {
  colIndex: number
  rowIndex: number
  entries: GridCellEntry[]
  day: string
  timeSlot: string
}

interface TimetableGridCSSProps {
  entries: TimetableGridEntry[]
  onCellClick: (day: string, timeSlot: string, entryIndices: number[], event?: MouseEvent<HTMLElement>, field?: 'subject' | 'room' | 'klass') => void
  onCellDoubleClick: (day: string, timeSlot: string, entryIndices: number[]) => void
  onEmptyCellClick?: (day: string, timeSlot: string) => void
  highlightedCell: { day: string; timeSlot: string } | null
  highlightedEntryIndices: Set<number>
  fitToView: boolean
  zoom?: number
  fixedDays?: string[]
  fixedTimeSlots?: string[]
  includeEmptyEntries?: boolean
  dimUnhighlighted?: boolean
  confirmedCategory?: 'subject' | 'room' | 'klass' | null
  intrinsic?: boolean
}

function slotSortValue(label: string, entries: TimetableGridEntry[]): number {
  const lessons = entries.flatMap((entry) => {
    if (normalizeTimeSlot(entry.time_slot_label) !== label || entry.lesson_number == null) return []
    return [entry.lesson_number]
  })
  if (lessons.length > 0) return Math.min(...lessons)
  const time = label.match(/(\d{1,2}):(\d{2})/)
  if (time?.[1] && time[2]) return parseInt(time[1], 10) * 60 + parseInt(time[2], 10)
  const num = label.match(/(\d+)/)
  if (num?.[1]) return parseInt(num[1], 10)
  return Number.MAX_SAFE_INTEGER - 1
}

function computeDimensions(
  entries: TimetableGridEntry[],
  fixedDays?: string[],
  fixedTimeSlots?: string[],
  includeEmptyEntries?: boolean,
): GridDimensions {
  const hasMeaningfulContent = (e: TimetableGridEntry) =>
    !!(e.teacher_code?.trim() || e.subject?.trim() || e.room?.trim() || e.class_name?.trim())

  const meaningfulEntries = includeEmptyEntries ? entries : entries.filter(hasMeaningfulContent)

  let days: string[]
  if (fixedDays) {
    days = [...fixedDays]
    if (meaningfulEntries.some(e => !e.day_of_week) && !days.includes(UNASSIGNED_DAY)) {
      days.push(UNASSIGNED_DAY)
    }
  } else {
    const hasUnassignedDay = meaningfulEntries.some(e => !e.day_of_week)
    days = [...new Set(
      meaningfulEntries
        .map(e => e.day_of_week)
        .filter((d): d is string => d !== null && d !== undefined && d !== UNASSIGNED_DAY)
    )]
      .sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b))
    if (hasUnassignedDay) days.push(UNASSIGNED_DAY)
  }

  let timeSlots: string[]
  if (fixedTimeSlots) {
    timeSlots = [...fixedTimeSlots]
    if (meaningfulEntries.some(e => !e.time_slot_label) && !timeSlots.includes(UNASSIGNED_TIME)) {
      timeSlots.push(UNASSIGNED_TIME)
    }
  } else {
    const hasUnassignedTime = meaningfulEntries.some(e => !e.time_slot_label)
    timeSlots = [...new Set(
      meaningfulEntries
        .map(e => normalizeTimeSlot(e.time_slot_label))
        .filter((t): t is string => t !== null && t !== undefined && t !== UNASSIGNED_TIME)
    )]
      .sort((a, b) => slotSortValue(a, meaningfulEntries) - slotSortValue(b, meaningfulEntries) || a.localeCompare(b, 'de', { numeric: true }))
    if (hasUnassignedTime) timeSlots.push(UNASSIGNED_TIME)
  }

  return { days, timeSlots }
}

function buildGridCells(
  entries: TimetableGridEntry[],
  dimensions: GridDimensions,
  includeEmptyEntries?: boolean,
): GridCell[] {
  const cellMap = new Map<string, GridCell>()

  entries.forEach((entry, index) => {
    const hasContent = entry.teacher_code?.trim() || entry.subject?.trim() || entry.room?.trim() || entry.class_name?.trim()
    if (!hasContent && !includeEmptyEntries) return

    const day = entry.day_of_week ?? UNASSIGNED_DAY
    const timeSlot = normalizeTimeSlot(entry.time_slot_label) ?? UNASSIGNED_TIME
    const axisIndex = dimensions.timeSlots.findIndex(slot => {
      if (slot === timeSlot || slot === entry.time_slot_label) return true
      const slotTime = normalizeTimeSlot(slot)
      return Boolean(slotTime) && slotTime === timeSlot
    })

    const colIndex = dimensions.days.indexOf(day) + 2
    let rowIndex = (axisIndex >= 0 ? axisIndex : dimensions.timeSlots.indexOf(timeSlot)) + 2

    if (colIndex < 2) return

    if (rowIndex < 2) {
      const unassignedIdx = dimensions.timeSlots.indexOf(UNASSIGNED_TIME)
      if (unassignedIdx >= 0) {
        rowIndex = unassignedIdx + 2
      } else {
        return
      }
    }

    const cellKey = `${colIndex}-${rowIndex}`
    if (!cellMap.has(cellKey)) {
      cellMap.set(cellKey, { colIndex, rowIndex, entries: [], day, timeSlot })
    }

    const cell = cellMap.get(cellKey)!
    cell.entries.push({ entry, index })
  })

  return Array.from(cellMap.values())
}

function computeOccupiedCells(cells: GridCell[]): Set<string> {
  const occupied = new Set<string>()
  for (const cell of cells) {
    occupied.add(`${cell.colIndex}-${cell.rowIndex}`)
  }
  return occupied
}

export function TimetableGridCSS({
  entries,
  onCellClick,
  onCellDoubleClick,
  onEmptyCellClick,
  highlightedCell,
  highlightedEntryIndices,
  fitToView,
  zoom = 1,
  fixedDays,
  fixedTimeSlots,
  includeEmptyEntries = false,
  dimUnhighlighted = false,
  confirmedCategory = null,
  intrinsic = false,
}: TimetableGridCSSProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [fitted, setFitted] = useState({ width: 0, height: 0 })

  const dimensions = useMemo(
    () => computeDimensions(entries, fixedDays, fixedTimeSlots, includeEmptyEntries),
    [entries, fixedDays, fixedTimeSlots, includeEmptyEntries],
  )
  const gridCells = useMemo(
    () => buildGridCells(entries, dimensions, includeEmptyEntries),
    [entries, dimensions, includeEmptyEntries],
  )
  const occupiedCells = useMemo(() => computeOccupiedCells(gridCells), [gridCells])

  useEffect(() => {
    if (!fitToView || intrinsic) {
      setScale(1)
      setFitted({ width: 0, height: 0 })
      return
    }
    const container = containerRef.current
    const grid = gridRef.current
    if (!container || !grid) return

    const update = () => {
      const contentWidth = Math.max(grid.scrollWidth, grid.offsetWidth)
      const contentHeight = Math.max(grid.scrollHeight, grid.offsetHeight)
      const next = computeFitScale(
        container.clientWidth,
        container.clientHeight,
        contentWidth,
        contentHeight,
      )
      setScale(prev => (prev === next ? prev : next))
      setFitted(prev => (
        prev.width === contentWidth && prev.height === contentHeight
          ? prev
          : { width: contentWidth, height: contentHeight }
      ))
    }

    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(container)
    ro.observe(grid)
    return () => ro.disconnect()
  }, [fitToView, intrinsic, entries, dimensions.days.length, dimensions.timeSlots.length])

  const handleEntryClick = useCallback((day: string, timeSlot: string, index: number, event?: MouseEvent<HTMLElement>, field?: 'subject' | 'room' | 'klass') => {
    onCellClick(day, timeSlot, [index], event, field)
  }, [onCellClick])

  const handleEntryDoubleClick = useCallback((day: string, timeSlot: string, index: number) => {
    onCellDoubleClick(day, timeSlot, [index])
  }, [onCellDoubleClick])

  if (entries.length === 0) {
    return null
  }

  const laidOut = fitToView || intrinsic
  const effectiveScale = intrinsic ? 1 : fitToView ? scale * zoom : zoom

  return (
    <div
      ref={containerRef}
      className={intrinsic ? 'inline-block w-max min-w-max' : `h-full min-h-0 w-full ${fitToView ? 'flex items-center justify-center overflow-hidden p-2' : 'overflow-auto'}`}
    >
      <div
        style={!intrinsic && fitToView && fitted.width > 0 ? {
          width: fitted.width * effectiveScale,
          height: fitted.height * effectiveScale,
          overflow: 'hidden',
          position: 'relative',
        } : undefined}
      >
      <div
        ref={gridRef}
        className="grid gap-px bg-gray-200"
        style={{
          width: laidOut ? 'max-content' : '100%',
          gridTemplateColumns: laidOut
            ? `80px repeat(${dimensions.days.length}, 180px)`
            : `80px repeat(${dimensions.days.length}, minmax(180px, 1fr))`,
          gridTemplateRows: `36px repeat(${dimensions.timeSlots.length}, minmax(40px, auto))`,
          transform: !intrinsic && effectiveScale !== 1 ? `scale(${effectiveScale})` : undefined,
          transformOrigin: 'top left',
          transition: 'transform 0.2s',
        }}
      >
        {/* Header: Zeit (top-left corner) */}
        <div className="sticky top-0 left-0 z-20 bg-gray-100 px-3 py-2 text-xs font-medium text-gray-500 flex items-center justify-center">
          Zeit
        </div>

        {/* Day Headers */}
        {dimensions.days.map((day, i) => (
          <div
            key={day}
            className="sticky top-0 z-10 px-3 py-2 text-xs font-medium text-center bg-gray-50 text-gray-600"
            style={{ gridColumn: i + 2, gridRow: 1 }}
          >
            {DAY_LABELS[day] || day}
          </div>
        ))}

        {/* Time Labels */}
        {dimensions.timeSlots.map((time, i) => (
          <div
            key={`${time}-${i}`}
            className="sticky left-0 z-10 px-3 py-2 text-xs font-mono flex items-center justify-center bg-gray-50 text-gray-500"
            style={{ gridColumn: 1, gridRow: i + 2 }}
          >
            {time === UNASSIGNED_TIME ? '?' : time.split('#')[0]}
          </div>
        ))}

        {/* Empty Cells */}
        {dimensions.timeSlots.map((time, timeIdx) =>
          dimensions.days.map((day, dayIdx) => {
            const colIndex = dayIdx + 2
            const rowIndex = timeIdx + 2
            const cellKey = `${colIndex}-${rowIndex}`

            if (occupiedCells.has(cellKey)) return null

            const isHighlighted = highlightedCell?.day === day && highlightedCell?.timeSlot === time
            const hasEmptyClick = !!onEmptyCellClick

            return (
              <div
                key={cellKey}
                className={`flex items-center justify-center text-xs relative z-0 bg-white ${
                  hasEmptyClick ? 'cursor-pointer group' : ''
                } ${
                  isHighlighted ? 'ring-2 ring-blue-500' : ''
                } ${hasEmptyClick ? 'hover:bg-gray-100' : ''}`}
                style={{ gridColumn: colIndex, gridRow: rowIndex }}
                onClick={() => onEmptyCellClick?.(day, time)}
              >
                <span className="text-gray-300 group-hover:hidden">—</span>
                {hasEmptyClick && (
                  <Plus className="w-4 h-4 text-gray-400 hidden group-hover:block transition-opacity" />
                )}
              </div>
            )
          })
        )}

        {/* Cell Containers with Entry Stacking */}
        {gridCells.map(({ colIndex, rowIndex, entries: cellEntries, day, timeSlot }) => {
          const isHighlighted = highlightedCell?.day === day && highlightedCell?.timeSlot === timeSlot

          return (
            <div
              key={`${colIndex}-${rowIndex}`}
              className={`flex flex-col gap-0.5 p-0.5 relative z-0 bg-white ${
                isHighlighted ? 'ring-2 ring-blue-500' : ''
              }`}
              style={{
                gridColumn: colIndex,
                gridRow: rowIndex,
              }}
            >
              {cellEntries.map(({ entry, index }) => {
                const isEntryHighlighted = highlightedEntryIndices.has(index)
                const isMultiPeriod = entry.is_multi_period && entry.lesson_count > 1
                const position = entry.multi_period_position
                const isCancelled = entry.is_cancelled
                
                let borderClass = ''
                if (isMultiPeriod) {
                  if (position === 'first') borderClass = 'border-l-2 border-l-blue-400 rounded-l-none'
                  else if (position === 'last') borderClass = 'border-l-2 border-l-blue-400 rounded-r-none'
                  else if (position === 'middle') borderClass = 'border-l-2 border-l-blue-400 rounded-none'
                }

                const exclusionHatch = entry.substitution_excluded ? exclusionHatchStyle : undefined
                const ampelBg =
                  entry.ampel === 'red'
                    ? 'bg-red-50 ring-1 ring-red-300 hover:bg-red-100'
                    : entry.ampel === 'yellow'
                      ? 'bg-amber-50 ring-1 ring-amber-300 hover:bg-amber-100'
                      : entry.ampel === 'green'
                        ? 'bg-green-50 ring-1 ring-green-200 hover:bg-green-100'
                        : 'bg-gray-50 hover:bg-gray-100'

                return (
                  <div
                    key={index}
                    data-grid-entry-index={index}
                    title={
                      entry.substitution_excluded
                        ? exclusionTitle
                        : entry.ampel_reasons?.length
                          ? entry.ampel_reasons.join(', ')
                          : undefined
                    }
                    style={exclusionHatch}
                    className={`text-xs rounded p-1 cursor-pointer transition-colors ${
                      isCancelled
                        ? 'bg-gray-100 opacity-50 line-through text-gray-500'
                        : isEntryHighlighted
                          ? 'bg-blue-100 ring-1 ring-blue-400'
                          : dimUnhighlighted && highlightedEntryIndices.size > 0
                            ? `${ampelBg} opacity-40`
                            : ampelBg
                    } ${borderClass}`}
                    onClick={e => handleEntryClick(day, timeSlot, index, e)}
                    onDoubleClick={() => handleEntryDoubleClick(day, timeSlot, index)}
                  >
                    {entry.is_clipped && entry.start_time && entry.end_time && (
                      <div className="text-[10px] font-mono text-gray-600">{String(entry.start_time).slice(0, 5)}–{String(entry.end_time).slice(0, 5)}</div>
                    )}
                    <div className={`flex flex-wrap items-center gap-1 min-h-0 ${isCancelled ? 'text-gray-500' : ''}`}>
                      {entry.teacher_code && (
                        <span className={`font-mono px-1 py-0.5 rounded text-xs ${isCancelled ? 'bg-gray-200 text-gray-500' : `${ENTRY_FIELD_COLORS.teacher_code.bg} ${ENTRY_FIELD_COLORS.teacher_code.text}`}`}>{entry.teacher_code}</span>
                      )}
                      {entry.subject ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${isCancelled ? 'bg-gray-200 text-gray-500' : confirmedCategory === 'subject' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : `${ENTRY_FIELD_COLORS.subject.bg} ${ENTRY_FIELD_COLORS.subject.text}`}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'subject')
                          }}
                        >
                          {entry.subject}
                        </span>
                      ) : !isCancelled ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${confirmedCategory === 'subject' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : 'border border-dashed border-green-300 text-green-700 bg-green-50/60'}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'subject')
                          }}
                        >
                          {confirmedCategory === 'subject' ? 'ohne Fach' : '+ Fach'}
                        </span>
                      ) : null}
                      {entry.subject_unclear && (
                        <span className="rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-800">?</span>
                      )}
                      {entry.room ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${isCancelled ? 'bg-gray-200 text-gray-500' : confirmedCategory === 'room' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : `${ENTRY_FIELD_COLORS.room.bg} ${ENTRY_FIELD_COLORS.room.text}`}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'room')
                          }}
                        >
                          {entry.room}
                        </span>
                      ) : !isCancelled ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${confirmedCategory === 'room' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : 'border border-dashed border-yellow-300 text-yellow-700 bg-yellow-50/60'}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'room')
                          }}
                        >
                          {confirmedCategory === 'room' ? 'ohne Raum' : '+ Raum'}
                        </span>
                      ) : null}
                      {entry.class_name ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${isCancelled ? 'bg-gray-200 text-gray-500' : confirmedCategory === 'klass' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : `${ENTRY_FIELD_COLORS.class_name.bg} ${ENTRY_FIELD_COLORS.class_name.text}`}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'klass')
                          }}
                        >
                          {entry.class_name}
                        </span>
                      ) : !isCancelled ? (
                        <span
                          className={`px-1 py-0.5 rounded text-xs ${confirmedCategory === 'klass' ? 'bg-green-100 text-green-800 ring-1 ring-green-500' : 'border border-dashed border-orange-300 text-orange-700 bg-orange-50/60'}`}
                          onClick={e => {
                            e.stopPropagation()
                            handleEntryClick(day, timeSlot, index, e, 'klass')
                          }}
                        >
                          {confirmedCategory === 'klass' ? 'ohne Klasse' : '+ Klasse'}
                        </span>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
      </div>
    </div>
  )
}
